import { Button, Dialog, InputAdornment, TextField } from "@mui/material";
import styles from "./Settings.module.css";
import emailIcon from "../../assets/login_mail.svg";
import { useState, useEffect } from "react";
import { settingsSchema, settingsSchemaType } from "./SettingsSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import passwordIcon from "../../assets/login_password.svg";
import {
  editCompanyProfileApi,
  uploadCompanyProfilePictureApi,
} from "../../api/profile";
import MobileInput from "./MobileInput";
import UpdateLogo from "./components/UpdateLogo";
import { Company } from "../../models/master";
import { useSnackbar } from "../../context/SnackbarContext";
import ResetPassword from "./components/ResetPassword";
import TickSVG from "../../assets/redesign/tick-03.svg";
import { useCreatedJobData } from "../../context/MasterContextProvider";
import { useRegion } from "../../context/RegionContext";
import { REGIONS } from "../../constants/regions";
import { countryCodeList } from "../../constants/constants";
import worldIcon from "../../assets/ksa/world.png";
import { images } from "../../assets/images";

interface Props {
  company: Company;
}

const SettingsForm = ({ company }: Props) => {
  // const lastname = localStorage.getItem("lastname");

  const localFirstname = company?.userName;
  // const lastname = localStorage.getItem("lastname");
  const { region } = useRegion();

  // Get default country code based on region
  const getDefaultCountryCode = (): string => {
    if (company?.countryCode) {
      return company.countryCode;
    }
    // Auto-select based on region
    const defaultCountryCode = region === REGIONS.AE ? "AE" : "SA";
    const selectedCountry = countryCodeList.find(
      (country) => country.code === defaultCountryCode
    );
    return selectedCountry?.label || "+971";
  };

  const { register, formState, watch } = useForm<settingsSchemaType>({
    mode: "onTouched",
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      name: localFirstname || "",
      phoneNumber: company?.companyContact || "",
      description: company?.description,
    },
  });

  const name = watch("name");
  const description = watch("description");

  const [phoneError, setPhoneError] = useState("");

  const [form, setForm] = useState({
    userName: "",
    email: "",
    phoneNumber: company?.companyContact || "",
    description: company?.description,
  });

  const [countryCode, setCountryCode] = useState(getDefaultCountryCode());

  // Update country code when region changes (only if company doesn't have a country code)
  useEffect(() => {
    if (!company?.countryCode) {
      const defaultCountryCode = region === REGIONS.AE ? "AE" : "SA";
      const selectedCountry = countryCodeList.find(
        (country) => country.code === defaultCountryCode
      );
      if (selectedCountry) {
        setCountryCode(selectedCountry.label);
      }
    }
  }, [region, company?.countryCode]);

  const setPhoneNumber = (number: string) => {
    setForm((prevState) => ({
      ...prevState,
      ["phoneNumber"]: number,
    }));
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { showSnackbar } = useSnackbar();

  const { setCreatedJob } = useCreatedJobData();

  const handleSave = async () => {
    if (description && description?.length > 1000) return;

    if (selectedFile) {
      const result = await uploadCompanyProfilePictureApi(selectedFile);

      if (result?.responseInfo?.httpCode === 200) {
        const fileUrl = result?.data?.fileUrl;
        const resp = await editCompanyProfileApi(
          name,
          form.phoneNumber,
          countryCode,
          description || "",
          name,
          company.primaryEmail,
          fileUrl
        );
        if (resp?.responseInfo?.httpCode === 200) {
          showSnackbar("Edited Company Profile.", "success");
          setCreatedJob((prevState) => prevState + 1);
        } else {
          showSnackbar(
            resp?.responseInfo?.description || "Something went wrong.",
            "error"
          );
        }
      } else {
        showSnackbar(
          result?.responseInfo?.description || "Failed to upload picture.",
          "error"
        );
      }
    } else {
      const resp = await editCompanyProfileApi(
        name,
        form.phoneNumber,
        countryCode,
        description || "",
        name,
        company.primaryEmail
      );
      if (resp?.responseInfo.httpCode === 200) {
        showSnackbar("Edited Company Profile.", "success");
        setCreatedJob((prevState) => prevState + 1);
      } else {
        showSnackbar(
          resp?.responseInfo?.description || "Something went wrong.",
          "error"
        );
      }
    }
  };

  const [openReset, setOpenReset] = useState(false);

  const handleOpenReset = () => {
    setOpenReset(true);
  };

  const handleCloseReset = () => {
    setOpenReset(false);
  };

  const [openDialog, setOpenDialog] = useState(false);

  const handleDone = () => {
    handleDialogOpen();
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <div className={styles.container}>
      <h2>Your Account</h2>
      <div className={styles.settingsContainer}>
        <p className={styles.bold}>Company Logo</p>
        <UpdateLogo
          setSelectedFile={setSelectedFile}
          initialData={
            company?.companyLogo ||
            images.companyLogoUpdate
          }
        />
        <div className={styles.formContainer}>
          <div className={styles.colForm}>
            <TextField
              label="User Name"
              placeholder="Please enter your name"
              {...register("name")}
              error={!!formState.errors.name}
              helperText={formState.errors.name?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      src={images.user}
                      alt="user_icon"
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              placeholder="Please enter your email"
              value={company.primaryEmail}
              disabled={true}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="user_icon" />
                  </InputAdornment>
                ),
              }}
            />
            <MobileInput
              setMainCountryCode={setCountryCode}
              setMainPhoneNumber={setPhoneNumber}
              setErrorMessage={setPhoneError}
              errorMessage={phoneError}
              mainCountryCode={company?.countryCode || ""}
              mainPhoneNumber={company?.companyContact || ""}
            />
            <TextField
              label="Password"
              fullWidth
              placeholder="**********"
              value={"*********"}
              disabled={true}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={passwordIcon} alt="password_icon" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <div
                    onClick={handleOpenReset}
                    className={styles.changePassword}
                  >
                    Change Password
                  </div>
                ),
              }}
            />
          </div>
          <div className={styles.colForm}>
            <TextField
              fullWidth
              label="Company Name"
              disabled={true}
              value={company.companyName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      src={images.briefcase}
                      alt="company_icon"
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Country"
              disabled={true}
              value={`${
                region === REGIONS.SA ? "Saudi Arabia" : "United Arab Emirates"
              } ${region === REGIONS.SA ? "🇸🇦" : "🇦🇪"}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      src={worldIcon}
                      alt="globe_icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="About Company"
              placeholder="Please enter about your company"
              {...register("description")}
              error={!!formState.errors.description}
              helperText={formState.errors.description?.message}
              multiline
              minRows={4}
              maxRows={8}
              className={styles.textArea}
              type="textarea"
            />
          </div>
        </div>
        <br />
        <Button onClick={handleSave} className={styles.saveBtn}>
          Save Changes
        </Button>
      </div>
      {openReset && (
        <Dialog open={openReset}>
          <ResetPassword handleDone={handleDone} onClose={handleCloseReset} />
        </Dialog>
      )}
      {openDialog && (
        <Dialog onClose={handleDialogClose} open={openDialog}>
          <div className="job_done_container">
            <img src={TickSVG} />
            <h3>Success! Your password has been changed.</h3>
            <Button onClick={handleDialogClose}>Done</Button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default SettingsForm;
